'use client';
import styled from '@emotion/styled';
import { darken, lighten } from 'color2k';
import { switchProp, ifProp } from 'styled-tools';
import { breakpoints } from '~/theme/breakpoints';
import { ButtonProps } from './Button';
import { ResetButton } from './ResetButton';

export const StyledButton = styled(ResetButton)<ButtonProps>(
    ({ theme, color }) => ({
        // Reset
        ...theme.mixins.useTextStyle('button'),
        padding: '7px 16px',
        fontFamily: theme.fonts.roboto,
        gap: '.75em', // multiple children
        transition: 'box-shadow, background-color',
        transitionDuration: '200ms',
        transitionTimingFunction: 'ease-out',
        textTransform: 'uppercase',
        willChange: 'box-shadow',
        color: color ? `${color} !important` : theme.colors.white,
        minHeight: '48px',
        justifyContent: 'center',
        svg: {
            height: 25,
            width: 25,
        },
        borderRadius: theme.sizes.cornerRadiusLarge,
        '&:disabled': {
            backgroundColor: theme.colors.disabled,
            '@media (hover: hover)': {
                '&:hover': {
                    cursor: 'not-allowed',
                    backgroundColor: theme.colors.disabled,
                    boxShadow: 'none',
                },
            },
        },
        ':focus-visible': {
            boxShadow: theme.shadows.accessibilityInset,
        },
    }),
    ifProp('small', ({ theme }) => ({
        minHeight: '32px',
        borderRadius: theme.sizes.cornerRadius,
        svg: {
            height: 16,
            width: 16,
        },
    })),
    switchProp('variant', {
        primary: ({ theme, isInverted }) => ({
            backgroundColor: theme.colors[isInverted ? 'white' : 'black'],
            color: theme.colors[isInverted ? 'black' : 'white'],
            textDecoration: 'none',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    color: theme.colors[isInverted ? 'black' : 'white'],
                    backgroundColor: lighten(theme.colors[isInverted ? 'white' : 'black'], 0.2),
                    textDecoration: 'none',
                },
                '&.hoverShadowStyle:hover': {
                    boxShadow: theme.shadows.buttonHover,
                    backgroundColor: theme.colors[isInverted ? 'white' : 'black'],
                },
            },
            '&:active,&:focus': {
                backgroundColor: lighten(theme.colors[isInverted ? 'white' : 'black'], 0.3),
            },
        }),
        'primary-inverse': ({ theme }) => ({
            backgroundColor: theme.colors.white,
            color: theme.colors.black,
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    color: theme.colors.black,
                    backgroundColor: lighten(theme.colors.white, 0.2),
                },
                '&.hoverShadowStyle:hover': {
                    boxShadow: theme.shadows.buttonHover,
                    backgroundColor: theme.colors.white,
                },
            },

            '&:active,&:focus': {
                backgroundColor: lighten(theme.colors.white, 0.3),
            },
        }),
        'black-friday': ({ theme }) => ({
            backgroundColor: theme.colors.blackFridayGold,
            color: theme.colors.black,
            textDecoration: 'none',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    boxShadow: theme.shadows.buttonHover,
                    color: theme.colors.black,
                },
            },
        }),
        christmas: ({ theme }) => ({
            backgroundColor: theme.colors.christmasGold,
            color: theme.colors.black,
            textDecoration: 'none',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    boxShadow: theme.shadows.buttonHover,
                    color: theme.colors.black,
                },
            },
        }),
        brand: ({ theme }) => ({
            color: theme.colors.white,
            backgroundColor: theme.colors.primary,
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.primary, 0.1),
                },
            },
            '&:active, &:focus': {
                backgroundColor: lighten(theme.colors.primary, 0.2),
            },
        }),
        campaign: ({ theme }) => ({
            color: theme.colors.white,
            backgroundColor: theme.colors.campaign,
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.campaign, 0.1),
                },
            },
            '&:active, &:focus': {
                backgroundColor: lighten(theme.colors.campaign, 0.2),
            },
        }),
        secondary: ({ theme }) => ({
            backgroundColor: theme.colors.grey100,
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.grey100, 0.1),
                },
                '&.hoverShadowStyle:hover': {
                    boxShadow: theme.shadows.buttonHover,
                    backgroundColor: theme.colors.grey100,
                },
            },
            '&:active, &:focus': {
                backgroundColor: lighten(theme.colors.grey100, 0.2),
            },
        }),
        plain: ({ theme }) => ({
            color: theme.colors.black,
            padding: 0,
            textTransform: 'none',
            svg: {
                '& path': {
                    fill: 'unset',
                },
            },
            '&:disabled': {
                backgroundColor: theme.colors.white,
                '@media (hover: hover)': {
                    '&:hover': {
                        cursor: 'not-allowed',
                        backgroundColor: theme.colors.white,
                    },
                },
            },
            [breakpoints.md]: {
                paddingTop: 0,
                paddingBottom: 0,
            },
        }),
        link: ({ theme }) => ({
            color: theme.colors.black,
            padding: 0,
            textTransform: 'none',
            textDecoration: 'none',
            '&:disabled': {
                backgroundColor: theme.colors.white,
                '@media (hover: hover)': {
                    '&:hover': {
                        cursor: 'not-allowed',
                        backgroundColor: theme.colors.white,
                    },
                },
            },
            [breakpoints.md]: {
                paddingTop: 0,
                paddingBottom: 0,
            },
        }),
        icon: ({ theme }) => ({
            borderRadius: 4,
            svg: {
                '& path': {
                    fill: 'unset',
                },
            },
            color: theme.colors.black,
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.black, 0.8),
                },
            },
            '&:active, &:focus': {
                backgroundColor: lighten(theme.colors.black, 0.5),
            },
        }),
        filter: ({ theme }) => ({
            ...theme.mixins.useTextStyle('filterButton'),
            borderRadius: 25,
            padding: '0 16px',
            paddingRight: '24px',
            minHeight: '35px',
            gap: '.5em',
            backgroundColor: theme.colors.black,
            boxShadow: theme.shadows.button,
            [breakpoints.sm]: {
                borderRadius: 25,
            },
            svg: {
                height: 24,
                width: 24,
            },
        }),
        plp: ({ theme }) => ({
            ...theme.mixins.useTextStyle('button'),
            backgroundColor: theme.colors.black,
            color: theme.colors.white,
            width: '100%',
            minHeight: '30px',
            padding: '8px 8px',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            [breakpoints.sm]: {
                padding: '14px 16px',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.black, 0.2),
                },
            },
        }),
        'inverse-plp': ({ theme }) => ({
            border: `1px solid ${theme.colors.grey150}`,
            color: theme.colors.black,
            ...theme.mixins.useTextStyle('bodySmall'),
            textTransform: 'none',
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: theme.colors.grey10,
                },
            },
            '&:focus': {
                backgroundColor: theme.colors.grey10,
            },
        }),
        pdp: ({ theme }) => ({
            ...theme.mixins.useTextStyle('large'),
            backgroundColor: theme.colors.black,
            color: theme.colors.white,
            width: '100%',
            padding: '14px 8px',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(theme.colors.black, 0.2),
                },
            },
        }),
        'pdp-secondary': ({ theme }) => ({
            ...theme.mixins.useTextStyle('large'),
            backgroundColor: theme.colors.white,
            border: `1px solid ${theme.colors.black}`,
            color: theme.colors.black,
            width: '100%',
            padding: '14px 8px',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: darken(theme.colors.white, 0.1),
                },
            },
            // Outline disabled: keep white fill, mute text + border (not filled like primary).
            '&:disabled': {
                color: theme.colors.disabled,
                borderColor: theme.colors.disabled,
                backgroundColor: theme.colors.white,
                '@media (hover: hover)': {
                    '&:hover': {
                        color: theme.colors.disabled,
                        borderColor: theme.colors.disabled,
                        backgroundColor: theme.colors.white,
                    },
                },
            },
        }),
        tag: ({ theme }) => ({
            fontWeight: theme.mixins.useFontWeight('Regular'),
            borderRadius: 25,
            padding: '10px 16px',
            minHeight: 40,
            textTransform: 'none',
            color: theme.colors.black,
            backgroundColor: theme.colors.brownLight,
            [breakpoints.sm]: {
                borderRadius: 25,
            },
        }),
        alternative: ({ theme, alternativeTextColor, alternativeBackgroundColor }) => ({
            color: alternativeTextColor || theme.colors.white,
            backgroundColor: alternativeBackgroundColor || theme.colors.black,
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    backgroundColor: lighten(alternativeBackgroundColor || theme.colors.black, 0.2),
                },
            },
            '&:active,&:focus': {
                backgroundColor: lighten(alternativeBackgroundColor || theme.colors.black, 0.3),
            },
        }),
        'new-secondary': ({ theme, isInverted }) => ({
            backgroundColor: 'none',
            color: theme.colors[isInverted ? 'white' : 'black'],
            border: `1px solid ${theme.colors[isInverted ? 'white' : 'black']}`,
            textDecoration: 'none',
            textTransform: 'none',
            '&:first-letter': {
                textTransform: 'uppercase',
            },
            '@media (hover: hover)': {
                '&:hover': {
                    color: theme.colors[isInverted ? 'white' : 'black'],
                    boxShadow: theme.shadows.buttonHover,
                    textDecoration: 'none',
                },
            },
        }),
        'new-link': ({ theme, isInverted }) => ({
            backgroundColor: 'none',
            color: theme.colors[isInverted ? 'white' : 'black'],
            minHeight: 0,
            textDecoration: 'none',
            padding: `0 0 3px 0`,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: 18,
            borderBottom: `2px solid ${theme.colors.primary}`,
            borderRadius: 0,
            '@media (hover: hover)': {
                '&:hover': {
                    textDecoration: 'none',
                    color: theme.colors[isInverted ? 'white' : 'black'],
                },
            },
        }),
        'new-link-black-friday': ({ theme }) => ({
            backgroundColor: 'none',
            color: theme.colors.white,
            minHeight: 0,
            textDecoration: 'none',
            padding: `0 0 3px 0`,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: 18,
            borderBottom: `2px solid ${theme.colors.blackFridayGold}`,
            borderRadius: 0,
            '@media (hover: hover)': {
                '&:hover': {
                    textDecoration: 'none',
                    color: theme.colors.white,
                },
            },
        }),
        'new-link-christmas': ({ theme }) => ({
            backgroundColor: 'none',
            color: theme.colors.white,
            minHeight: 0,
            textDecoration: 'none',
            padding: `0 0 3px 0`,
            fontWeight: 'bold',
            textTransform: 'none',
            fontSize: 18,
            borderBottom: `2px solid ${theme.colors.christmasGold}`,
            borderRadius: 0,
            '@media (hover: hover)': {
                '&:hover': {
                    textDecoration: 'none',
                    color: theme.colors.white,
                },
            },
        }),
    }),
    switchProp('size', {
        small: ({ theme }) => ({
            ...theme.mixins.useTextStyle('small'),
        }),
        large: ({ theme }) => ({
            ...theme.mixins.useTextStyle('large'),
            padding: '12px 16px',
            width: '100%',
        }),
    }),
    ifProp('fullWidth', {
        width: '100%',
    }),
);

export const StyledButtonContent = styled.span({
    alignItems: 'center',
    display: 'inline-flex',
    gap: '0.75em', // multiple children
});

export const StyledIcon = styled.span<ButtonProps>(
    {
        padding: '1em 1.5em',
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        transition: '0.2s ease-in-out',
        transformOrigin: 'center',
    },
    switchProp('variant', {
        primary: {
            paddingLeft: '1.2em',
            paddingRight: '1.2em',
            '&:first-of-type': {
                paddingRight: 0,
                marginRight: '-0.5em',
            },
            '&:last-of-type': {
                paddingLeft: 0,
                marginLeft: '-0.5em',
            },
            '&:first-of-type:last-of-type': {
                padding: '1em',
                marginLeft: 0,
                marginRight: 0,
            },
            path: {
                fill: 'currentColor',
            },
        },

        secondary: {
            paddingLeft: '1.2em',
            paddingRight: '1.2em',
            '&:first-of-type': {
                paddingRight: 0,
                marginRight: '-0.5em',
            },
            '&:last-of-type': {
                paddingLeft: 0,
                marginLeft: '-0.5em',
            },
            '&:first-of-type:last-of-type': {
                padding: '1em',
                marginLeft: 0,
                marginRight: 0,
            },
            path: {
                fill: 'currentColor',
            },
        },
        plain: {
            padding: '1em 0.5em',
            '&:first-of-type': {
                marginRight: 10,
            },
            '&:last-of-type': {
                marginLeft: 20,
            },
            '&:first-of-type:last-of-type': {
                padding: '1em',
                marginLeft: 0,
                marginRight: 0,
            },
        },
    }),
);
